import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ArrowRight, Phone, Zap, Shield, Building2, Droplets, Factory, Train, Wifi, Pickaxe, Gauge, Play, Pause, Star, ChevronLeft, ChevronRight, Quote, AlertTriangle, Sun, Radio, Building } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { fadeInUp, staggerContainer } from '../utils/cn';

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // Video carousel state
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
   const [isPlaying, setIsPlaying] = useState(true);
   const [isVideoLoaded, setIsVideoLoaded] = useState(false);
   const [failedVideos, setFailedVideos] = useState(new Set());
   const videoRef = useRef(null);
   const retryTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Division slider state
  const [currentDivisionSlide, setCurrentDivisionSlide] = useState(0);
  const divisionSliderRef = useRef(null);
  
  // Client logos state
  const [logoUnderHeading, setLogoUnderHeading] = useState(new Set());
  const clientHeadingRef = useRef(null);
  const logoSliderRef = useRef(null);
  const [animationX, setAnimationX] = useState(0);
  
  // Supplier logos state
  const [supplierLogosUnderHeading, setSupplierLogosUnderHeading] = useState(new Set());
  const supplierHeadingRef = useRef(null);
  const supplierSliderRef = useRef(null);
  
  const videos = [
    {
      url: 'https://res.cloudinary.com/dsmgydskc/video/upload/v1756306660/vecteezy_oil-refinery-at-sunset_69464843_ajlk3e.mp4',
      title: 'Oil Refinery Operations'
    },
    {
      url: 'https://res.cloudinary.com/dsmgydskc/video/upload/v1756306321/vecteezy_industrial-oil-refinery-at-twilight-with-illuminated-steel_49534754_v1sgnu.mp4',
      title: 'Industrial Infrastructure'
    },
    {
      url: 'https://res.cloudinary.com/dsmgydskc/video/upload/v1756306277/vecteezy_smart-artificial-intelligence-futuristic-concept-oil_2020806_e8wdhn.mp4',
      title: 'Smart Technology Solutions'
    },
    {
      url: 'https://res.cloudinary.com/dsmgydskc/video/upload/v1756306749/vecteezy_a-factory-with-smoke-coming-out-of-the-chimneys_49642763_sjgkem.mov',
      title: 'Manufacturing Excellence'
    },
    {
      url: 'https://res.cloudinary.com/dsmgydskc/video/upload/v1756306720/vecteezy_nighttime-industrial-plant-with-illuminated-smoke-stacks-and_49534752_slmaor.mp4',
      title: 'Industrial Operations'
    }
  ];
  
  // Add error handling for video loading
   const handleVideoError = () => {
     console.warn(`Failed to load video: ${videos[currentVideoIndex].title}`);
     
     // Mark this video as failed
     setFailedVideos(prev => new Set([...prev, currentVideoIndex]));
     
     // Skip to next video if current one fails
     if (isPlaying) {
       // Clear any existing retry timeout
       if (retryTimeoutRef.current) {
         clearTimeout(retryTimeoutRef.current);
       }
       
       // Move to next video after a short delay
       retryTimeoutRef.current = setTimeout(() => {
         const nextIndex = (currentVideoIndex + 1) % videos.length;
         setCurrentVideoIndex(nextIndex);
       }, 1000);
     }
   };
   
   // Reset failed videos when manually selecting a video
    const goToVideo = (index) => {
      setCurrentVideoIndex(index);
      setIsVideoLoaded(false);
      // Remove from failed list to allow retry
      setFailedVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    };
  
  // Auto-cycle videos every 10 seconds
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentVideoIndex((prevIndex) => 
          prevIndex === videos.length - 1 ? 0 : prevIndex + 1
        );
      }, 10000);
    } else {
      clearInterval(intervalRef.current);
    }
    
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, videos.length]);
  

  
  // Handle video loading
  useEffect(() => {
    if (videoRef.current) {
      setIsVideoLoaded(false);
      videoRef.current.load();
    }
  }, [currentVideoIndex]);
  
  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  };
  
  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  
  // Function to get current division image
  const getCurrentDivisionImage = (division, imageIndex) => {
    if (division.images && division.images[imageIndex]) {
      return `/divisions/${division.folder}/${division.images[imageIndex]}`;
    }
    return '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg';
  };
  

  const divisions = [
    {
      key: 'telecommunication',
      icon: Phone,
      path: '/divisions/telecommunication',
      color: 'from-primary to-accent',
      folder: 'Telecommunications',
      images: [
        'communications-tower-2024-10-22-17-32-42-utc.jpg',
        'data-center-with-multiple-rows-of-fully-operationa-2023-11-27-05-16-14-utc.jpg',
        'fiber-optics-2024-10-21-03-46-49-utc.jpg',
        'telecommunication-equipment-for-5g-radio-network-2024-12-03-04-12-51-utc.jpg',
        'bundle-of-fibre-optic-cables-2024-06-20-03-08-53-utc.jpg'
      ]
    },
    {
      key: 'power',
      icon: Zap,
      path: '/divisions/power',
      color: 'from-primary to-accent',
      folder: 'Power - Solar',
      images: [
        'solar-panels-producing-green-renewable-energy-sol-2023-11-27-05-31-29-utc.jpg',
        'electrical-substation-distribution-power-plant-wit-2024-07-25-01-00-57-utc.jpg',
        'engineer-in-charge-of-solar-panel-installation-the-2023-11-27-05-02-11-utc.jpg',
        'arrays-of-blue-solar-panels-2023-11-27-05-06-18-utc.jpg',
        'power-plant-near-coast-2023-11-27-05-22-28-utc.jpg'
      ]
    },
    {
      key: 'hazardous',
      icon: Shield,
      path: '/divisions/hazardous',
      color: 'from-primary to-accent',
      folder: 'Hazardous',
      images: [
        'chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-18-08-utc.jpg',
        'industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg',
        'fpso-tanker-vessel-near-oil-platform-rig-at-night-2023-11-27-04-59-12-utc.jpg',
        'detail-of-chemical-plant-in-twilight-2023-11-27-05-32-28-utc.jpg',
        'liquid-natural-gas-globe-shape-containers-2023-11-27-05-37-11-utc.jpg'
      ]
    },
    {
      key: 'commercial',
      icon: Building2,
      path: '/divisions/commercial',
      color: 'from-primary to-accent',
      folder: 'Commercial - Electrical',
      images: [
        'man-an-electrical-technician-working-in-a-switchb-2023-11-27-04-54-33-utc.jpg',
        'the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg',
        'robotic-arm-modern-industrial-technology-automate-2023-11-27-05-21-51-utc.jpg',
        'hvac-technician-installing-large-modern-heat-pump-2023-11-27-05-04-45-utc.jpeg',
        'vertical-of-a-power-station-for-power-supply-2023-11-27-05-27-38-utc.jpg'
      ]
    }
  ];

  // Auto-cycle division slider every 12 seconds for better visibility
  useEffect(() => {
    const divisionInterval = setInterval(() => {
      setCurrentDivisionSlide((prevSlide) => 
        prevSlide === divisions.length - 1 ? 0 : prevSlide + 1
      );
    }, 12000);
    
    return () => clearInterval(divisionInterval);
  }, [divisions.length]);

  // Intersection observer for client logos
  useEffect(() => {
    // Function to check which individual logos are under heading
    const checkLogoPosition = () => {
      if (clientHeadingRef.current && logoSliderRef.current) {
        const headingRect = clientHeadingRef.current.getBoundingClientRect();
        const sliderRect = logoSliderRef.current.getBoundingClientRect();
        
        // Simplified approach: if slider is visible and overlaps with heading area
        const isSliderVisible = sliderRect.top < window.innerHeight && sliderRect.bottom > 0;
        const headingBottom = headingRect.bottom;
        const sliderTop = sliderRect.top;
        
        // Check if logos are in the area where heading would be
        if (isSliderVisible && sliderTop < headingBottom + 100 && sliderRect.bottom > headingRect.top - 100) {
          // For simplicity, let's make logos under heading show color when they're in the center area
          const logosUnderHeading = new Set();
          
          // Get viewport center
          const viewportCenterX = window.innerWidth / 2;
          const headingCenterX = headingRect.left + headingRect.width / 2;
          
          // Get all logo containers
          const logoContainers = logoSliderRef.current.querySelectorAll('[class*="flex-shrink-0"]');
          
          logoContainers.forEach((container, index) => {
            const containerRect = container.getBoundingClientRect();
            const containerCenterX = containerRect.left + containerRect.width / 2;
            
            // Check if logo is near the heading horizontally (within 200px)
            if (Math.abs(containerCenterX - headingCenterX) < 200) {
              logosUnderHeading.add(index);
            }
          });
          
          console.log('Logos under heading (simplified):', Array.from(logosUnderHeading));
          setLogoUnderHeading(logosUnderHeading);
        } else {
          setLogoUnderHeading(new Set());
        }
      }
    };

    // Check position every 100ms instead of every frame for better performance
    const interval = setInterval(checkLogoPosition, 100);
    
    // Initial check
    checkLogoPosition();
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Intersection observer for supplier logos
  useEffect(() => {
    // Function to check which individual supplier logos are under heading
    const checkSupplierLogoPosition = () => {
      if (supplierHeadingRef.current && supplierSliderRef.current) {
        const headingRect = supplierHeadingRef.current.getBoundingClientRect();
        const sliderRect = supplierSliderRef.current.getBoundingClientRect();
        
        // Simplified approach: if slider is visible and overlaps with heading area
        const isSliderVisible = sliderRect.top < window.innerHeight && sliderRect.bottom > 0;
        const headingBottom = headingRect.bottom;
        const sliderTop = sliderRect.top;
        
        // Check if logos are in the area where heading would be
        if (isSliderVisible && sliderTop < headingBottom + 100 && sliderRect.bottom > headingRect.top - 100) {
          // For simplicity, let's make logos under heading show color when they're in the center area
          const supplierLogosUnderHeading = new Set();
          
          // Get viewport center
          const viewportCenterX = window.innerWidth / 2;
          const headingCenterX = headingRect.left + headingRect.width / 2;
          
          // Get all supplier logo containers
          const logoContainers = supplierSliderRef.current.querySelectorAll('[class*="flex-shrink-0"]');
          
          logoContainers.forEach((container, index) => {
            const containerRect = container.getBoundingClientRect();
            const containerCenterX = containerRect.left + containerRect.width / 2;
            const distance = Math.abs(containerCenterX - headingCenterX);
            
            // Check if logo is near the heading horizontally (within 200px)
            if (distance < 200) {
              supplierLogosUnderHeading.add(index);
            }
          });
          
          setSupplierLogosUnderHeading(supplierLogosUnderHeading);
        } else {
          setSupplierLogosUnderHeading(new Set());
        }
      }
    };

    // Check position every 100ms instead of every frame for better performance
    const supplierInterval = setInterval(checkSupplierLogoPosition, 100);
    
    // Initial check
    checkSupplierLogoPosition();
    
    return () => {
      clearInterval(supplierInterval);
    };
  }, []);

  const industries = [
    { 
      key: 'oilGas', 
      icon: Factory,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <path d="M25 50C25 50 35 40 50 40C65 40 75 50 75 50C75 50 65 60 50 60C35 60 25 50 25 50Z" fill="currentColor"/>
          <circle cx="50" cy="30" r="4" fill="currentColor"/>
          <rect x="47" y="34" width="6" height="20" fill="currentColor"/>
          <path d="M40 70L60 70M42 75L58 75M45 80L55 80" stroke="currentColor" strokeWidth="1"/>
        </svg>
      )
    },
    { 
      key: 'water', 
      icon: Droplets,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <path d="M50 20C42 20 37 25 37 32C37 40 42 45 50 45C58 45 63 40 63 32C63 25 58 20 50 20Z" fill="currentColor"/>
          <path d="M30 60C30 60 40 50 50 50C60 50 70 60 70 60C70 70 60 80 50 80C40 80 30 70 30 60Z" fill="currentColor"/>
          <circle cx="25" cy="40" r="2" fill="currentColor"/>
          <circle cx="75" cy="45" r="2" fill="currentColor"/>
        </svg>
      )
    },
    { 
      key: 'infrastructure', 
      icon: Building2,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <rect x="20" y="30" width="15" height="40" fill="currentColor"/>
          <rect x="40" y="20" width="20" height="50" fill="currentColor"/>
          <rect x="65" y="35" width="15" height="35" fill="currentColor"/>
          <rect x="22" y="32" width="2" height="2" fill="white"/>
          <rect x="26" y="32" width="2" height="2" fill="white"/>
          <rect x="42" y="25" width="3" height="3" fill="white"/>
          <rect x="47" y="25" width="3" height="3" fill="white"/>
        </svg>
      )
    },
    { 
      key: 'petrochemicals', 
      icon: Factory,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <rect x="30" y="40" width="40" height="30" fill="currentColor"/>
          <circle cx="40" cy="30" r="7" fill="currentColor"/>
          <circle cx="60" cy="30" r="7" fill="currentColor"/>
          <rect x="37" y="22" width="6" height="15" fill="currentColor"/>
          <rect x="57" y="22" width="6" height="15" fill="currentColor"/>
        </svg>
      )
    },
    { 
      key: 'railway', 
      icon: Train,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <rect x="20" y="45" width="60" height="15" rx="7" fill="currentColor"/>
          <circle cx="30" cy="65" r="4" fill="currentColor"/>
          <circle cx="70" cy="65" r="4" fill="currentColor"/>
          <rect x="25" y="47" width="7" height="5" fill="white"/>
          <rect x="35" y="47" width="7" height="5" fill="white"/>
          <path d="M10 70L90 70" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      key: 'telecommunications', 
      icon: Wifi,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <rect x="45" y="20" width="10" height="40" fill="currentColor"/>
          <path d="M35 30L65 30M37 35L63 35M40 40L60 40" stroke="currentColor" strokeWidth="1"/>
          <circle cx="50" cy="70" r="2" fill="currentColor"/>
          <path d="M42 70C42 70 46 66 50 66C54 66 58 70 58 70" stroke="currentColor" strokeWidth="1" fill="none"/>
        </svg>
      )
    },
    { 
      key: 'mining', 
      icon: Pickaxe,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <path d="M20 60L50 30L80 60L80 80L20 80Z" fill="currentColor"/>
          <rect x="40" y="70" width="20" height="10" fill="white"/>
          <circle cx="35" cy="50" r="2" fill="white"/>
          <circle cx="65" cy="55" r="2" fill="white"/>
        </svg>
      )
    },
    { 
      key: 'utilities', 
      icon: Gauge,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <circle cx="50" cy="50" r="25" fill="currentColor"/>
          <path d="M50 35L55 45L45 45Z" fill="white"/>
          <rect x="47" y="52" width="6" height="13" fill="white"/>
          <path d="M30 50L70 50M50 30L50 70" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      key: 'power', 
      icon: Zap,
      bgImage: (
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" fill="none">
          <path d="M50 20L60 45L40 45Z" fill="currentColor"/>
          <path d="M50 80L40 55L60 55Z" fill="currentColor"/>
          <circle cx="50" cy="50" r="8" fill="white"/>
        </svg>
      )
    }
  ];

  // Testimonials state and data
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  
  const testimonials = [
    {
      id: 1,
      name: 'Ahmed Al-Rashid',
      role: 'Operations Director',
      company: 'ADNOC',
      industry: 'Oil & Gas',
      rating: 5,
      review: 'Global Technology has been instrumental in modernizing our communication infrastructure across multiple facilities. Their expertise in hazardous area equipment and process control systems is unmatched.',
      photo: (
        <img
          src="/Our client logos/1. ADNOC.png"
          alt="ADNOC Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    },
    {
      id: 2,
      name: 'Sarah Mitchell',
      role: 'Infrastructure Manager',
      company: 'DEWA ',
      industry: 'Smart Cities',
      rating: 5,
      review: 'Their smart city solutions have significantly improved our operational efficiency and citizen services. The team\'s professionalism and technical expertise exceeded our expectations.',
      photo: (
        <img
          src="/Our client logos/5. DEWA-logo.jpeg"
          alt="DEWA Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    },
    {
      id: 3,
      name: 'Dr. Mohammad Hassan',
      role: 'Chief Technology Officer',
      company: 'SABIC',
      industry: 'Petrochemicals',
      rating: 5,
      review: 'Exceptional safety solutions and reliable support throughout our digital transformation journey. Global Technology consistently delivers innovative solutions that drive our business forward.',
      photo: (
        <img
          src="/Our client logos/17. SABIC.png"
          alt="SABIC Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    },
    {
      id: 4,
      name: 'Jennifer Chen',
      role: 'Network Engineering Lead',
      company: 'SEWA',
      industry: 'Telecommunications',
      rating: 5,
      review: 'Professional team with deep technical expertise. They consistently deliver beyond expectations and provide excellent ongoing support for our telecommunications infrastructure.',
      photo: (
        <img
          src="/Our client logos/6. sewa_logo.png"
          alt="SEWA Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    },
    {
      id: 5,
      name: 'Robert Thompson',
      role: 'Project Manager',
      company: 'Aramco',
      industry: 'Energy',
      rating: 5,
      review: 'Outstanding project management and technical delivery. Their solutions have enhanced our operational safety and efficiency across multiple offshore platforms.',
      photo: (
        <img
          src="/Our client logos/15. Saudi Aramco.jpeg"
          alt="Saudi Aramco Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    },
    {
      id: 6,
      name: 'Fatima Al-Zahra',
      role: 'Systems Engineer',
      company: 'PETRONAS',
      industry: 'Transportation',
      rating: 5,
      review: 'Their railway communication and signaling systems have revolutionized our operations. The implementation was seamless and the ongoing support is exceptional.',
      photo: (
        <img
          src="/Our client logos/23. PETRONAS.png"
          alt="PETRONAS Logo"
          className="w-full h-full object-contain p-2 bg-white rounded-full"
          loading="lazy"
        />
      )
    }
  ];

  // Auto-cycle testimonials every 8 seconds
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 8000);
    
    return () => clearInterval(testimonialInterval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonialIndex(index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Responsive Layout */}
      <section className="relative min-h-screen overflow-hidden">
        
        {/* Video Background - Full Screen */}
        <div className="absolute inset-0 w-full h-full">
          <motion.video
            ref={videoRef}
            key={currentVideoIndex}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop={true}
            playsInline
            onLoadedData={handleVideoLoaded}
            onError={handleVideoError}
            initial={{ opacity: 0 }}
            animate={{ opacity: isVideoLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <source src={videos[currentVideoIndex].url} type="video/mp4" />
            Your browser does not support the video tag.
          </motion.video>
          
          {/* Loading Overlay */}
          {!isVideoLoaded && (
            <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 mx-auto mb-4"></div>
                <p className="text-sm font-medium">Loading video...</p>
              </div>
            </div>
          )}
          
          {/* Fallback background for loading */}
          {!isVideoLoaded && (
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: 'url("https://c4.wallpaperflare.com/wallpaper/354/359/832/landscape-industry-the-evening-silhouettes-wallpaper-preview.jpg")'
              }}
            />
          )}
          
          {/* Video Overlay */}
          <div className="absolute inset-0 opacity-10"></div>
          
        </div>
        
        {/* Main Content - Centered */}
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="text-center px-6 max-w-4xl mx-auto">
            
            {/* Small Badge */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            >
              <span className="inline-block px-4 py-2 bg-global-dark-blue/30 backdrop-blur-sm rounded-lg text-sm font-semibold text-white border border-white/20">
                <motion.span
                  animate={{ opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  Since 1999 • Global Excellence
                </motion.span>
              </span>
            </motion.div>
          
            {/* Main Heading */}
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight text-white"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <motion.span 
                className="block mb-2"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.7 }}
              >
                {t('hero.tagline')}
              </motion.span>
              <motion.span 
                className="block"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.9 }}
              >
                Engineering Solutions
              </motion.span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p 
              className="text-lg sm:text-xl mb-8 text-white/90 leading-relaxed font-medium max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
            >
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              >
                {t('hero.subtitle')}
              </motion.span>
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.0 }}
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <Link to="/services">
                  <Button 
                    size="lg" 
                    className="bg-white hover:bg-gradient-to-r from-global-green to-global-light-blue text-black px-8 py-4 text-lg font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
                  >
                    <motion.div
                      className="absolute inset-0 bg-global-light-blue/20"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.6 }}
                    />
                    <span className="relative z-10 flex items-center">
                      {t('hero.cta')}
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="ml-2"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </motion.div>
                    </span>
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="bg-gradient-to-r from-global-green to-global-light-blue border-2 text-white hover:bg-global-dark-blue px-8 py-4 text-lg font-bold rounded-lg transition-all duration-300 backdrop-blur-sm"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Contact us 
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
        
        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-black/30 rounded-full flex justify-center">
            <motion.div
              className="w-1 h-3 bg-global-dark-blue/50 rounded-full mt-2"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

      {/* Client Logos Slider */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="container-max">
          <motion.div
            className="text-center mb-12 pt-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            ref={clientHeadingRef}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Clients
            </h2>
            <p className="text-lg text-slate max-w-2xl mx-auto">
              Trusted by leading organizations across various industries
            </p>
          </motion.div>

          {/* Client Logos Slider */}
          <div className="relative">
            {/* Slider Container */}
            <div className="overflow-hidden">
              <motion.div
                className="flex space-x-8 md:space-x-12"
                animate={{
                  x: [0, -2000]
                }}
                transition={{
                  duration: 30,
                  repeat: Infinity,
                  ease: "linear"
                }}
                ref={logoSliderRef}
              >
                {/* First set of logos */}
                {[
                  "1. ADNOC.png",
                  "2.ADWEA.png", 
                  "3. ADDC.png",
                  "4. abu-dhabi-airports.png",
                  "4a BOROUGE LOGO.png",
                  "5. DEWA-logo.jpeg",
                  "6. sewa_logo.png",
                  "7. CPECC.jpeg",
                  "8. ENEC logo.jpg",
                  "9. dragon oil.png",
                  "10. Saipem_logo.png",
                  "11. Alderley.jpg",
                  "12. Petrofac.png",
                  "13. robtstone.jpeg",
                  "14. AlASAB.jpeg",
                  "15. Saudi Aramco.jpeg",
                  "16. SATORP.png",
                  "17. SABIC.png",
                  "18. Sadara.jpeg",
                  "19. SAPCO.jpeg",
                  "20. KJO logo.jpeg",
                  "21. MEW kuwait.jpeg",
                  "22. MAADEN.png",
                  "23. PETRONAS.png",
                  "24. KBR.png",
                  "25. ABB.png",
                  "PETRONASH.png",
                  "SWCC.jpg"
                ].map((logo, index) => (
                  <motion.div
                    key={`first-${index}`}
                    className="flex-shrink-0 w-32 h-20 md:w-40 md:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={`/Our client logos/${logo}`}
                      alt={`Client logo ${index + 1}`}
                      className={`max-w-full max-h-full object-contain p-2 transition-all duration-300 ${
                            logoUnderHeading.has(index)
                              ? 'filter-none' 
                              : 'filter grayscale hover:grayscale-0'
                          }`}
                      loading="lazy"
                    />
                  </motion.div>
                ))}
                
                {/* Duplicate set for seamless loop */}
                {[
                  "1. ADNOC.png",
                  "2.ADWEA.png", 
                  "3. ADDC.png",
                  "4. abu-dhabi-airports.png",
                  "4a BOROUGE LOGO.png",
                  "5. DEWA-logo.jpeg",
                  "6. sewa_logo.png",
                  "7. CPECC.jpeg",
                  "8. ENEC logo.jpg",
                  "9. dragon oil.png",
                  "10. Saipem_logo.png",
                  "11. Alderley.jpg",
                  "12. Petrofac.png",
                  "13. robtstone.jpeg",
                  "14. AlASAB.jpeg",
                  "15. Saudi Aramco.jpeg",
                  "16. SATORP.png",
                  "17. SABIC.png",
                  "18. Sadara.jpeg",
                  "19. SAPCO.jpeg",
                  "20. KJO logo.jpeg",
                  "21. MEW kuwait.jpeg",
                  "22. MAADEN.png",
                  "23. PETRONAS.png",
                  "24. KBR.png",
                  "25. ABB.png",
                  "PETRONASH.png",
                  "SWCC.jpg"
                ].map((logo, index) => (
                  <motion.div
                    key={`second-${index}`}
                    className="flex-shrink-0 w-32 h-20 md:w-40 md:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={`/Our client logos/${logo}`}
                      alt={`Client logo ${index + 1}`}
                      className={`max-w-full max-h-full object-contain p-2 transition-all duration-300 ${
                            logoUnderHeading.has(index + 28)
                              ? 'filter-none' 
                              : 'filter grayscale hover:grayscale-0'
                          }`}
                      loading="lazy"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <div className="absolute inset-y-0 left-0 flex items-center">
              <motion.button
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-charcoal p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 -ml-4"
                whileHover={{ scale: 1.1, x: -2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  // Pause animation temporarily for manual control
                }}
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.button>
            </div>
            
            <div className="absolute inset-y-0 right-0 flex items-center">
              <motion.button
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-charcoal p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 -mr-4"
                whileHover={{ scale: 1.1, x: 2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  // Pause animation temporarily for manual control
                }}
              >
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </section>
     

      {/* About Preview Section  */}
      <section className="section-padding bg-white relative overflow-hidden mt-16">
        {/* Optimized 3D Particle Animation Background */}
        
        <div className="container-max relative z-10">
          {/* Single Column Layout with Cards Above Content */}
          <div className="max-w-6xl mx-auto">

            {/* Section Title */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-charcoal block">
                  {t('about.title')}
                </span>
                <motion.span 
                  className="block text-global-green"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  Excellence
                </motion.span>
              </h2>
              <div className="w-24 h-1 bg-global-green mx-auto mb-8"></div>
            </motion.div>

            {/* Statistics Cards - Now Above Content */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {[
                {
                  number: '25+',
                  label: 'Years of Experience',
                  description: 'Serving industries since 1999',
                  gradient: 'from-global-green to-global-light-blue',
                  svgIcon: (
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                  )
                },
                {
                  number: '3',
                  label: 'Global Offices',
                  description: 'UAE, USA, and KSA',
                  gradient: 'from-global-light-blue to-global-dark-blue',
                  svgIcon: (
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1 4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )
                },
                {
                  number: 'ISO',
                  label: 'Certified Quality',
                  description: 'ISO 9001, 14001, 45001',
                  gradient: 'gradient-to-r from-global-green to-global-light-blue',
                  svgIcon: (
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )
                }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="group relative w-full min-w-96"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  {/* Parallelogram Background with Enhanced Patterns */}
                  <div 
                    className="absolute inset-0 shadow-xl"
                    style={{
                      backgroundImage: 'none',
                      
                      backgroundSize: '20px 20px, 30px 30px, 100% 100%',
                      clipPath: 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)',
                      transform: 'skewX(-10deg)'
                    }}
                  ></div>
                
                  {/* Parallelogram Card Content with Hero-Style Background */}
                  <div 
                    className="relative p-4 text-white min-w-full"
                    style={{
                      clipPath: 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)',
                      transform: 'skewX(-10deg)'
                    }}
                  >
                    {/* Base gradient background matching hero-section */}
                    <div className="absolute inset-0 bg-gradient-to-r from-global-green to-global-light-blue"></div>
                    
                    {/* Primary Diagonal Pattern Layer */}
                    <div 
                      className="absolute inset-0 opacity-15"
                      style={{
                       
                        backgroundSize: 'clamp(40px, 8vw, 80px) clamp(40px, 8vw, 80px), clamp(60px, 10vw, 100px) clamp(60px, 10vw, 100px)'
                      }}
                    />
                    
                    {/* Grid Pattern Layer */}
                    <div 
                      className="absolute inset-0 opacity-8"
                      style={{
                        backgroundImage: 'none',
                        
                      }}
                    />
                    
                    {/* Cross Pattern Layer */}
                    <div 
                      className="absolute inset-0 opacity-6"
                      style={{
                        backgroundImage: 'none',
                     
                        backgroundSize: 'clamp(70px, 12vw, 120px) clamp(70px, 12vw, 120px), clamp(90px, 15vw, 150px) clamp(90px, 15vw, 150px)'
                      }}
                    /> 
                    <div style={{ transform: 'skewX(10deg)' }} className="text-center">
                      {/* Enhanced Number with Glow Effect */}
                      <motion.div 
                        className="text-2xl md:text-3xl font-black text-white mb-1"
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 0.4, type: "spring" }}
                      >
                        <span className="relative z-10">{stat.number}</span>
                      </motion.div>
                      
                      {/* Enhanced Label with Underline Effect */}
                      <h3 className="text-base font-bold text-white mb-2 group-hover:text-gray-200 transition-colors duration-300 relative">
                        {stat.label}
                      </h3>
                      
                      {/* Enhanced Description */}
                      <p className="text-white/90 leading-snug group-hover:text-white transition-colors duration-300 font-medium text-sm">
                        {stat.description}
                      </p>
                      
                      {/* Additional Floating Geometric Elements */}
                      <div className="absolute top-4 right-4 w-8 h-8 opacity-20 group-hover:opacity-40 transition-opacity duration-500">
                        <svg viewBox="0 0 24 24" className="w-full h-full text-white">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" fill="none"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Content Section - Now Below Cards */}
            <motion.div
              className="text-center max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
            >
              {/* Company Overview */}
              <p className="text-lg md:text-xl text-slate leading-relaxed mb-8 font-light">
                {t('about.overview')}
              </p>

              {/* CTA Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/about"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-global-green to-global-light-blue text-white rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-500 group"
                >
                  <span className="mr-3">{t('common.learnMore')}</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>
          </div>          
        </div>
      </section>

      {/* Divisions Section - Enhanced with Auto-Moving Slider */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              {t('divisions.title')}
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('divisions.subtitle')}
            </p>
          </motion.div>

          {/* Auto-Moving Slider Container */}
          <div className="relative overflow-hidden rounded-2xl">
            <motion.div
              ref={divisionSliderRef}
              className="flex transition-transform duration-[2000ms] ease-in-out will-change-transform"
              style={{
                transform: `translate3d(-${currentDivisionSlide * 100}%, 0, 0)`
              }}
            >
              {divisions.map((division, divisionIndex) => {
                const Icon = division.icon;
                return (
                  <div
                    key={division.key}
                    className="w-full flex-shrink-0"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[500px]">
                      {/* Division Info Card */}
                      <motion.div
                        className="order-2 lg:order-1 will-change-transform"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ 
                          opacity: divisionIndex === currentDivisionSlide ? 1 : 0,
                          x: divisionIndex === currentDivisionSlide ? 0 : -50
                        }}
                        transition={{ duration: 1.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                        style={{
                          transform: `translate3d(${divisionIndex === currentDivisionSlide ? 0 : -50}px, 0, 0)`
                        }}
                      >
                        <Link to={division.path}>
                          <Card className="h-full hover:scale-105 transition-all duration-300 bg-white border-2 border-transparent hover:border-global-green shadow-xl">
                            <CardHeader className="text-center p-8">
                              <div className="w-20 h-20 bg-gradient-to-r from-global-green to-global-light-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                                <Icon className="w-10 h-10 text-white" />
                              </div>
                              <CardTitle className="text-2xl mb-4 text-charcoal">
                                {t(`divisions.${division.key}.title`)}
                              </CardTitle>
                              <CardDescription className="text-lg text-slate leading-relaxed">
                                {t(`divisions.${division.key}.description`)}
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="px-8 pb-8">
                              <Button className="w-full bg-gradient-to-r from-global-green to-global-light-blue text-white font-semibold py-3 rounded-lg transition-all duration-300">
                                {t('common.learnMore')} <ArrowRight className="ml-2 w-4 h-4" />
                              </Button>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>

                      {/* Image Slider */}
                      <motion.div
                        className="order-1 lg:order-2 relative will-change-transform"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ 
                          opacity: divisionIndex === currentDivisionSlide ? 1 : 0,
                          x: divisionIndex === currentDivisionSlide ? 0 : 50
                        }}
                        transition={{ duration: 1.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                        style={{
                          transform: `translate3d(${divisionIndex === currentDivisionSlide ? 0 : 50}px, 0, 0)`
                        }}
                      >
                        <div className="relative h-[400px] rounded-xl overflow-hidden shadow-2xl">
                          {/* Main Image */}
                          <div className="relative w-full h-full">
                            <img
                              src={getCurrentDivisionImage(division, 0)}
                              alt={t(`divisions.${division.key}.title`)}
                              className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                              onError={(e) => {
                                e.target.src = '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg';
                              }}
                            />
                            <div className="absolute inset-0 bg-global-dark-blue/50"></div>
                          </div>

                          {/* Image Thumbnails */}
                          <div className="absolute bottom-4 left-4 right-4">
                            <div className="flex space-x-2 overflow-x-auto pb-2">
                              {division.images.slice(1, 4).map((image, imageIndex) => (
                                <div
                                  key={imageIndex}
                                  className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 border-white/50 hover:border-white transition-all duration-300 cursor-pointer"
                                >
                                  <img
                                    src={getCurrentDivisionImage(division, imageIndex + 1)}
                                    alt={`${division.key} ${imageIndex + 1}`}
                                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                    onError={(e) => {
                                      e.target.src = '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg';
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Division Label */}
                          <div className="absolute top-4 left-4">
                            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                              <span className="text-sm font-semibold text-charcoal">
                                {t(`divisions.${division.key}.title`)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* Slider Navigation Dots */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-3">
                {divisions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentDivisionSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentDivisionSlide
                        ? 'bg-gradient-to-r from-global-green to-global-light-blue scale-125'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentDivisionSlide(
                currentDivisionSlide === 0 ? divisions.length - 1 : currentDivisionSlide - 1
              )}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentDivisionSlide(
                currentDivisionSlide === divisions.length - 1 ? 0 : currentDivisionSlide + 1
              )}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Access Grid - Parallelogram Cards */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {divisions.map((division, index) => {
              const Icon = division.icon;
              return (
                <motion.div
                  key={division.key}
                  variants={fadeInUp}
                  className={`cursor-pointer transition-all duration-300 ${
                    index === currentDivisionSlide ? 'scale-105' : 'hover:scale-105'
                  }`}
                  onClick={() => setCurrentDivisionSlide(index)}
                >
                  {/* Parallelogram Card Container */}
                  <div className="relative h-24 overflow-hidden">
                    {/* Parallelogram Background with Hero Pattern */}
                    <div 
                      className={`absolute inset-0 transition-all duration-300 ${
                        index === currentDivisionSlide 
                          ? 'bg-gradient-to-r from-global-green to-global-light-blue'
                          : 'bg-white hover:bg-global-light-blue'
                      }`}
                      style={{
                        clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)',
                        transform: 'skewX(-5deg)'
                      }}
                    >
                      {/* Hero-style Geometric Pattern */}
                      <div 
                        className={`absolute inset-0 ${
                          index === currentDivisionSlide ? 'opacity-15' : 'opacity-8'
                        }`}
                        style={{
                          backgroundImage: 'none',
                          
                          backgroundSize: '40px 40px, 60px 60px'
                        }}
                      />
                      
                      {/* Additional Grid Pattern */}
                      <div 
                        className={`absolute inset-0 ${
                          index === currentDivisionSlide ? 'opacity-8' : 'opacity-4'
                        }`}
                        style={{
                        
                        }}
                      />
                    </div>
                    
                    {/* Card Content */}
                    <div 
                      className="relative h-full flex flex-col items-center justify-center text-center p-4"
                      style={{
                        transform: 'skewX(5deg)'
                      }}
                    >
                      <Icon className={`w-6 h-6 mb-1 transition-colors duration-300 ${
                        index === currentDivisionSlide ? 'text-white' : 'text-global-green'
                      }`} />
                      <p className={`text-xs font-medium transition-colors duration-300 ${
                        index === currentDivisionSlide ? 'text-white' : 'text-global-dark-blue'
                      }`}>
                        {t(`divisions.${division.key}.title`)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Client Industries - Enhanced Image Gallery */}
      <section className="section-padding bg-white relative">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-0 right-0 w-96 h-96 bg-global-green/5 rounded-full blur-3xl"
          ></div>
          <div 
            className="absolute bottom-0 left-0 w-96 h-96 bg-global-light-blue/5 rounded-full blur-3xl"
          ></div>
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              {t('industries.title')}
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('industries.subtitle')}
            </p>
          </motion.div>

          {/* Enhanced Image-Based Industry Cards */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                key: 'oilGas',
                title: 'Oil & Gas',
                description: 'Comprehensive solutions for upstream, midstream, and downstream operations',
                folder: 'Oil and Gas',
                featuredImage: 'elevated-night-view-of-an-oil-refinery-complex-2024-07-29-20-20-48-utc.JPG',
                gradient: 'from-global-green/90 to-global-dark-blue/90',
                icon: Factory
              },
              {
                key: 'telecommunication',
                title: 'Telecommunication',
                description: 'Cutting-edge telecom infrastructure and communication solutions',
                folder: 'Telecommunications',
                featuredImage: 'data-center-with-multiple-rows-of-fully-operationa-2023-11-27-05-16-14-utc.jpg',
                gradient: 'from-global-green/90 to-global-light-blue/90',
                icon: Radio
              },
              {
                key: 'power',
                title: 'Power',
                description: 'Advanced power generation and distribution solutions',
                folder: 'Power - Solar',
                featuredImage: 'solar-panels-producing-green-renewable-energy-sol-2023-11-27-05-31-29-utc.jpg',
                gradient: 'from-global-dark-blue/90 to-global-light-blue/90',
                icon: Zap
              },
              {
                key: 'water',
                title: 'Water',
                description: 'Advanced systems for water treatment, distribution, and management',
                folder: 'General',
                featuredImage: 'dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg',
                gradient: 'from-global-light-blue/90 to-global-green/90',
                icon: Droplets
              },
              {
                key: 'petrochemicals',
                title: 'Petrochemicals',
                description: 'Specialized solutions for chemical processing and manufacturing',
                folder: 'Hazardous',
                featuredImage: 'industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg',
                gradient: 'from-global-green/90 to-global-light-blue/90',
                icon: Factory
              },
              {
                key: 'mining',
                title: 'Mining',
                description: 'Robust solutions for harsh mining environments and operations',
                folder: 'General',
                featuredImage: 'industrial-stone-mining-truck-in-the-middle-of-h-2023-11-27-04-58-18-utc.jpg',
                gradient: 'from-global-green/90 to-global-light-blue/90',
                icon: Pickaxe
              },
              {
                key: 'infrastructure',
                title: 'Infrastructure',
                description: 'Smart infrastructure solutions for modern cities and developments',
                folder: 'General',
                featuredImage: 'city-overpass-at-night-2023-11-27-05-10-18-utc.jpg',
                gradient: 'from-global-dark-blue/90 to-global-light-blue/90',
                icon: Building
              },
              {
                key: 'railway',
                title: 'Railway',
                description: 'Advanced railway communication and signaling systems',
                folder: 'General',
                featuredImage: 'metro-railway-train-in-dubai-city-in-uae-2023-11-27-05-18-15-utc.jpg',
                gradient: 'from-global-dark-blue/90 to-global-green/90',
                icon: Train
              },
              {
                key: 'utilities',
                title: 'Utilities',
                description: 'Comprehensive utility management and infrastructure solutions',
                folder: 'Commercial - Electrical',
                featuredImage: 'man-an-electrical-technician-working-in-a-switchb-2023-11-27-04-54-33-utc.jpg',
                gradient: 'from-global-dark-blue/90 to-global-light-blue/90',
                icon: Gauge
              }
            ].map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.key}
                  className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-global-green/30 overflow-hidden"
                  variants={fadeInUp}
                  whileHover={{ 
                    scale: 1.02,
                    y: -8
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  {/* Image Container with Lazy Loading */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      src={`/divisions/${industry.folder}/${industry.featuredImage}`}
                      alt={industry.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.7 }}
                    />
                    
                    {/* Fallback Background */}
                    <div 
                      className="absolute inset-0 bg-global-green/20 hidden items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <Icon className="w-16 h-16 text-global-green/60" />
                    </div>
                    
                    {/* Solid Overlay */}
                <div className={`absolute inset-0 bg-global-green opacity-0 group-hover:opacity-80 transition-opacity duration-500`}></div>
                    
                    {/* Icon Overlay */}
                    <motion.div
                      className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
                      initial={{ scale: 0, rotate: -180 }}
                      whileHover={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Icon className="w-6 h-6 text-global-green" />
                    </motion.div>
                    
                    {/* Hover Content */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      initial={{ y: 20 }}
                      whileHover={{ y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="text-center text-white px-4">
                        <motion.h4 
                          className="text-lg font-bold mb-2"
                          initial={{ opacity: 0, y: 10 }}
                          whileHover={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          {industry.title}
                        </motion.h4>
                        <motion.p 
                          className="text-sm opacity-90 leading-relaxed"
                          initial={{ opacity: 0, y: 10 }}
                          whileHover={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          {industry.description}
                        </motion.p>
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-global-green/10 rounded-lg flex items-center justify-center mr-3 border border-global-green/20">
                        <Icon className="w-5 h-5 text-global-green" />
                      </div>
                      <h3 className="text-lg font-semibold text-charcoal group-hover:text-global-green transition-colors duration-300">
                        {industry.title}
                      </h3>
                    </div>
                    
                    <p className="text-sm text-slate leading-relaxed mb-4">
                      {industry.description}
                    </p>
                    
                    {/* View More Link */}
                    <motion.div
                      className="flex items-center text-global-green text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300"
                      whileHover={{ x: 5 }}
                    >
                      <span>Explore Solutions</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </motion.div>
                  </div>
                  
                  {/* Subtle Border Animation */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-global-green/20 transition-colors duration-500"></div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Our Suppliers Slider */}
  
        <div className="container-max mt-20">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4 px-6" ref={supplierHeadingRef}>
              Our Suppliers
            </h2>
            <p className="text-lg text-slate max-w-2xl mx-auto">
              Partnering with world-class suppliers to deliver exceptional quality and reliability
            </p>
          </motion.div>

          {/* Suppliers Logos Slider */}
          <div className="relative">
            {/* Slider Container */}
            <div className="overflow-hidden">
              <motion.div
                className="flex space-x-8 md:space-x-12"
                ref={supplierSliderRef}
                animate={{
                  x: [0, -100 * 25] // Move by 25 logo widths (assuming ~100px per logo with spacing)
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 60, // Slower for better visibility
                    ease: "linear",
                  },
                }}
              >
                {/* First set of supplier logos */}
                {[
                  "ABB.png",
                  "APC.png",
                  "Allen-Bradley_logo.png",
                  "Baldor.png",
                  "Belden.png",
                  "Borri.png",
                  "British General.jpg",
                  "CEG logo.png",
                  "Ducab.jpg",
                  "Eaton.jpg",
                  "Enersys.jpeg",
                  "Fluke.png",
                  "GE.png",
                  "GNB.png",
                  "Gewiss.jpg",
                  "Gutor.png",
                  "Hioki.png",
                  "Honeywell-Logo.png",
                  "Hoppecke.png",
                  "Kepco_Logo.png",
                  "Kyocera_logo.svg.png",
                  "La Marche.png",
                  "Lexseco.png",
                  "MK Electric.jpg",
                  "Moxa.png"
                ].map((logo, index) => (
                  <motion.div
                    key={`first-${index}`}
                    className="flex-shrink-0 w-32 h-20 md:w-40 md:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={`/Suppliers logo/${logo}`}
                      alt={`Supplier logo ${index + 1}`}
                      className={`max-w-full max-h-full object-contain p-2 transition-all duration-300 ${
                        supplierLogosUnderHeading.has(index) ? 'filter-none' : 'filter grayscale hover:grayscale-0'
                      }`}
                      loading="lazy"
                    />
                  </motion.div>
                ))}
                
                {/* Second set of supplier logos for seamless loop */}
                {[
                  "Oman cable.jpg",
                  "Osram.jpg",
                  "Panasonic.jpg",
                  "Philips.jpg",
                  "Prysmian.jpg",
                  "Riyadh cables.jpg",
                  "Saft.png",
                  "Sonnenschein_Logo.jpg",
                  "Statron.png",
                  "aeg-power-solutions.png",
                  "alcad-logo.jpg",
                  "benning.png",
                  "emerson.png",
                  "exide-logo-png.png",
                  "legrand-logo.png",
                  "leviton-logo.jpg",
                  "mean-well.png",
                  "megger_logo.jpg",
                  "mitsubishi-electric.png",
                  "nidec-logo.jpg",
                  "nvent.png",
                  "phoenix-contact-logo.png",
                  "powrsonic.png",
                  "schneider-logo.png",
                  "siemens.png",
                  "skf_logo.png",
                  "thermon.png",
                  "toshiba.jpg",
                  "weg-logo.png"
                ].map((logo, index) => (
                  <motion.div
                    key={`second-${index}`}
                    className="flex-shrink-0 w-32 h-20 md:w-40 md:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={`/Suppliers logo/${logo}`}
                      alt={`Supplier logo ${index + 26}`}
                      className={`max-w-full max-h-full object-contain p-2 transition-all duration-300 ${
                        supplierLogosUnderHeading.has(index + 25) ? 'filter-none' : 'filter grayscale hover:grayscale-0'
                      }`}
                      loading="lazy"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <div className="absolute inset-y-0 left-0 flex items-center">
              <motion.button
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-charcoal p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 -ml-4"
                whileHover={{ scale: 1.1, x: -2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  // Pause animation temporarily for manual control
                }}
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.button>
            </div>
            
            <div className="absolute inset-y-0 right-0 flex items-center">
              <motion.button
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-charcoal p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 -mr-4"
                whileHover={{ scale: 1.1, x: 2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  // Pause animation temporarily for manual control
                }}
              >
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>      
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-72 h-72 bg-global-green/3 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-72 h-72 bg-global-light-blue/3 rounded-full blur-3xl"></div>
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Hear from industry leaders who trust Global Technology to deliver exceptional results.
            </p>
          </motion.div>

          {/* Main Testimonial Display */}
          <div className="relative max-w-4xl mx-auto mb-12">
            <motion.div
              key={currentTestimonialIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 md:p-12 border border-global-green/10"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                {/* Customer Photo */}
                <motion.div
                  className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden shadow-lg flex-shrink-0"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  {testimonials[currentTestimonialIndex].photo}
                </motion.div>

                {/* Testimonial Content */}
                <div className="flex-1 text-center md:text-left">
                  {/* Quote Icon */}
                   <motion.div
                     animate={{ y: [0, -5, 0] }}
                     transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                   >
                     <Quote className="w-8 h-8 text-global-green/30 mb-4 mx-auto md:mx-0" />
                   </motion.div>
                  
                  {/* Rating */}
                  <div className="flex justify-center md:justify-start mb-4">
                    {[...Array(testimonials[currentTestimonialIndex].rating)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                      >
                        <Star className="w-5 h-5 text-global-green fill-current" />
                      </motion.div>
                    ))}
                  </div>

                  {/* Review Text */}
                  <blockquote className="text-lg md:text-xl text-charcoal mb-6 leading-relaxed italic">
                    "{testimonials[currentTestimonialIndex].review}"
                  </blockquote>

                  {/* Customer Info */}
                  <div className="border-t border-global-green/10 pt-6">
                    <h4 className="text-xl font-bold text-charcoal mb-1">
                      {testimonials[currentTestimonialIndex].name}
                    </h4>
                    <p className="text-global-green font-medium mb-1">
                      {testimonials[currentTestimonialIndex].role}
                    </p>
                    <p className="text-slate text-sm">
                      {testimonials[currentTestimonialIndex].company} • {testimonials[currentTestimonialIndex].industry}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Navigation Arrows - Hidden on mobile */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group border border-global-green/20 hover:border-global-green/40 hidden md:flex"
            >
              <ChevronLeft className="w-6 h-6 text-global-green group-hover:text-global-dark-blue transition-colors duration-300" />
            </button>
            
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group border border-global-green/20 hover:border-global-green/40 hidden md:flex"
            >
              <ChevronRight className="w-6 h-6 text-global-green group-hover:text-global-dark-blue transition-colors duration-300" />
            </button>
          </div>

          {/* Mobile Navigation Buttons */}
          <div className="flex justify-center space-x-4 mb-8 md:hidden">
            <button
              onClick={prevTestimonial}
              className="w-10 h-10 bg-global-green/10 hover:bg-global-green/20 rounded-full flex items-center justify-center border border-global-green/20 hover:border-global-green/40 transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5 text-global-green" />
            </button>
            <button
              onClick={nextTestimonial}
              className="w-10 h-10 bg-global-green/10 hover:bg-global-green/20 rounded-full flex items-center justify-center border border-global-green/20 hover:border-global-green/40 transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5 text-global-green" />
            </button>
          </div>

          {/* Testimonial Indicators */}
          <div className="flex justify-center space-x-3 mb-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentTestimonialIndex
                    ? 'bg-global-green scale-125'
                    : 'bg-global-green/30 hover:bg-global-green/60'
                }`}
              />
            ))}
          </div>

          {/* Testimonial Cards Grid - Show 3 at a time */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-500 p-6 border border-global-green/10 hover:border-global-green/20 group cursor-pointer"
                variants={fadeInUp}
                whileHover={{ 
                  scale: 1.02,
                  y: -5
                }}
                onClick={() => goToTestimonial(index)}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0">
                    {testimonial.photo}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-charcoal text-sm">{testimonial.name}</h5>
                    <p className="text-global-green text-xs">{testimonial.company}</p>
                  </div>
                </div>
                
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-global-green fill-current" />
                  ))}
                </div>
                
                <p className="text-slate text-sm leading-relaxed line-clamp-3">
                  "{testimonial.review.substring(0, 120)}..."
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-global-green/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-gradient-to-r from-global-green to-global-light-blue z-5 w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: 'none',
                    
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: 'none',
                    
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="inline-block mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30">
                    Ready to Get Started?
                  </span>
                </motion.div>
                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Ready to Start Your Project?
                </motion.h2>
                
                <motion.p 
                  className="text-base lg:text-lg mb-6 text-gray-100 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Get in touch with our experts to discuss your engineering needs
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                        {t('common.contactUs')}
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/services">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm transition-all duration-300">
                        {t('common.getQuote')}
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>

            
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;